import { useEffect } from "react"

import includes from "lodash-es/includes"
import keys from "lodash-es/keys"
import { useMount } from "react-use"

import { FALLBACK_LANG } from "$/constants"
import { i18n } from "$/i18n"
import { setLuxonLocale } from "$/utils/lang"

import { type TUserState, useIsFakeParticipant, useUser } from "@/3514/hooks/index"
import { isUserCoach, isUserManager, isUserParticipant } from "@/3514/utils"
import { type Language } from "@/models/state"

function useAppLanguage(): void {
    const isFakeParticipant: boolean = useIsFakeParticipant()

    const { user, isLoaded }: TUserState = useUser()

    useMount((): void => {
        document.documentElement.lang = i18n.language
    })

    useEffect((): void => {
        if (!isLoaded) return void 0

        setLuxonLocale(user.languageCode)

        if (isUserCoach(user) || isUserManager(user)) return void 0

        if (isFakeParticipant) {
            void i18n.changeLanguage(FALLBACK_LANG).then((): void => {
                document.documentElement.lang = FALLBACK_LANG
            })
            return void 0
        }

        const targetLanguage: Language =
            isUserParticipant(user) && includes<Language>(keys(user.cohortLanguages) as Language[], user.languageCode)
                ? user.languageCode
                : FALLBACK_LANG

        void i18n.changeLanguage(targetLanguage).then((): void => {
            document.documentElement.lang = targetLanguage
        })

        return void 0
    }, [user, isFakeParticipant, isLoaded])
}

export { useAppLanguage }
